import {
    ColumnType,
    YascSectionOrderStrategy,
    yascClassDisplayName,
    yascClassFormSectionTitle,
    yascClassSectionOrderStrategy,
    yascCollection,
    yascDisplayName,
    yascEntityName,
    yascField,
    yascFormGrid,
    yascFormIgnore,
    yascFormKind,
    yascFormRows,
    yascMax,
    yascMaxLength,
    yascMin,
    yascNested,
    yascOptionsKey,
    yascRequired,
    yascSectionOrder,
    yascTableColumn,
    yascTableDisplayFn,
    yascTableHidden,
    yascTableIgnore,
    yascTableIndex,
    yascTableLevelOfDetail,
    yascTableWidth,
} from '@yasc/yasc-material-components';
import {CompanyMinimumRes} from '../cp';
import {WorkflowStatusMinRes} from './status.contract';
import {ProjectRole} from '../../../enums';
import {BaseTaskRes} from './task';
import {InvoiceDetailRes} from '../cp/invoice.contract';

function formatCurrency(value?: number | null): string {
    if (value === undefined || value === null) return '';
    return new Intl.NumberFormat('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(value);
}

function displayCompany(value?: CompanyMinimumRes | number | null): string {
    if (value === undefined || value === null) return '';
    if (typeof value === 'number') return `${value}`;
    return value.name ?? '';
}

function displayWorkflowStatus(value?: WorkflowStatusMinRes | null): string {
    return value?.name ?? '';
}

function displayUser(value: ProjectTeamMember | number | null | undefined): string {
    if (value === undefined || value === null) return '';
    if (typeof value === 'number') return `${value}`;
    return value.userName ?? value.user?.userName ?? value.user?.email ?? (value.userId ? `${value.userId}` : '');
}

function displayRole(value: ProjectRole | ProjectTeamMember | number | null | undefined): string {
    if (value === undefined || value === null) return '';
    const role = typeof value === 'object' ? value.roleId : value;
    if (role === undefined || role === null) return '';
    return ProjectRole[role] ?? `${role}`;
}

@yascClassDisplayName('projectTabTeamTitle')
@yascClassFormSectionTitle('projectTabTeamTitle')
export class ProjectTeamMember {
    @yascField()
    @yascDisplayName('general_backendId')
    @yascTableIgnore(true)
    @yascFormIgnore()
    id?: number;

    @yascField()
    @yascDisplayName('general_employee')
    @yascTableIndex(0)
    @yascTableWidth(240)
    @yascTableColumn(ColumnType.Text)
    @yascTableDisplayFn(displayUser)
    @yascFormKind('autocomplete')
    @yascOptionsKey('projectTeamUsers')
    @yascRequired()
    @yascFormGrid({row: 1, width: 2, fullRow: true})
    userId: number | null = null;

    @yascField()
    @yascDisplayName('general_role')
    @yascTableIndex(1)
    @yascTableColumn(ColumnType.Text)
    @yascTableDisplayFn(displayRole)
    @yascFormKind('select')
    @yascOptionsKey('projectRoles')
    @yascRequired()
    @yascFormGrid({row: 2, width: 2, fullRow: true})
    roleId: ProjectRole | null = null;

    user?: {id?: number; userName?: string; email?: string};
    userName?: string;

    constructor(init: Partial<ProjectTeamMember> = {}) {
        Object.assign(this, init);
        this.userId = init.userId ?? init.user?.id ?? null;
        this.roleId = init.roleId ?? null;
        this.userName = init.userName ?? init.user?.userName ?? init.user?.email;
    }
}

@yascClassDisplayName('projectTableTask')
@yascClassFormSectionTitle('projectTableTask')
export class ProjectTaskItem {
    @yascField()
    @yascDisplayName('crm_task_id_label')
    @yascTableIgnore(true)
    @yascFormIgnore()
    id = '';

    @yascField()
    @yascDisplayName('general_name')
    @yascTableIndex(0)
    @yascTableColumn(ColumnType.Text)
    @yascTableWidth(260)
    @yascFormIgnore()
    name = '';

    @yascField()
    @yascDisplayName('crm_contract_category_label')
    @yascTableIndex(1)
    @yascTableColumn(ColumnType.Text)
    @yascFormIgnore()
    category?: string;

    @yascField()
    @yascDisplayName('crm_task_currentStart_label')
    @yascTableIndex(2)
    @yascTableColumn(ColumnType.Date)
    @yascFormIgnore()
    currentStart?: string;

    @yascField()
    @yascDisplayName('crm_task_currentEnd_label')
    @yascTableIndex(3)
    @yascTableColumn(ColumnType.Date)
    @yascFormIgnore()
    currentEnd?: string;

    @yascField()
    @yascDisplayName('crm_task_currentEst_label')
    @yascTableIndex(4)
    @yascTableColumn(ColumnType.Number)
    @yascFormIgnore()
    currentEstimated?: number;

    constructor(init: Partial<ProjectTaskItem | BaseTaskRes> = {}) {
        Object.assign(this, init);
        this.id = `${(init as any).id ?? ''}`;
        this.name = (init as any).name ?? '';
    }
}

@yascClassDisplayName('projectTabInvoicesTitle')
@yascClassFormSectionTitle('projectTabInvoicesTitle')
export class ProjectInvoiceItem {
    @yascField()
    @yascDisplayName('general_backendId')
    @yascTableIgnore(true)
    @yascFormIgnore()
    id?: number;

    @yascField()
    @yascDisplayName('invoiceNumber')
    @yascTableIndex(0)
    @yascTableColumn(ColumnType.Text)
    @yascFormIgnore()
    invoiceNumber = '';

    @yascField()
    @yascDisplayName('general_createdAt')
    @yascTableIndex(1)
    @yascTableColumn(ColumnType.Date)
    @yascFormIgnore()
    created?: string;

    @yascField()
    @yascDisplayName('invoicePaymentTarget')
    @yascTableIndex(2)
    @yascTableColumn(ColumnType.Date)
    @yascFormIgnore()
    paymentTarget?: string;

    @yascField()
    @yascDisplayName('invoicePaidOn')
    @yascTableIndex(3)
    @yascTableColumn(ColumnType.Date)
    @yascFormIgnore()
    paidOn?: string;

    @yascField()
    @yascDisplayName('projectTableVolume')
    @yascTableIndex(4)
    @yascTableColumn(ColumnType.Decimal)
    @yascTableDisplayFn(formatCurrency)
    @yascFormIgnore()
    totalBrutto?: number;

    constructor(init: Partial<ProjectInvoiceItem | InvoiceDetailRes> = {}) {
        Object.assign(this, init);
        this.invoiceNumber = (init as any).invoiceNumber ?? '';
    }
}

@yascEntityName('general_project')
@yascClassDisplayName('general_project')
@yascClassFormSectionTitle('projectTabGeneralTitle')
@yascClassSectionOrderStrategy(YascSectionOrderStrategy.ExplicitThenTitleAsc)
export class Project {
    @yascField()
    @yascDisplayName('general_backendId')
    @yascTableIndex(99)
    @yascTableColumn(ColumnType.Number)
    @yascTableHidden(true)
    @yascFormIgnore()
    id = 0;

    @yascField()
    @yascDisplayName('general_name')
    @yascTableIndex(1)
    @yascTableColumn(ColumnType.Text)
    @yascTableWidth(260)
    @yascRequired()
    @yascMaxLength(150)
    @yascFormGrid({row: 1, width: 1})
    name = '';

    @yascField()
    @yascDisplayName('general_internalName')
    @yascTableIgnore(true)
    @yascMaxLength(100)
    @yascFormGrid({row: 1, width: 1})
    internalName = '';

    @yascField()
    @yascDisplayName('projectTableCustomer')
    @yascTableIndex(0)
    @yascTableColumn(ColumnType.Text)
    @yascTableDisplayFn(displayCompany)
    @yascFormIgnore()
    customer: CompanyMinimumRes | null = null;

    @yascField()
    @yascDisplayName('projectTableCustomer')
    @yascTableIgnore(true)
    @yascFormKind('autocomplete')
    @yascOptionsKey('projectCustomers')
    @yascRequired()
    @yascFormGrid({row: 2, width: 1})
    customerId: number | null = null;

    @yascField()
    @yascDisplayName('general_currentWorkflow')
    @yascTableIgnore(true)
    @yascFormKind('select')
    @yascOptionsKey('projectWorkflows')
    @yascRequired()
    @yascFormGrid({row: 2, width: 1})
    workflowId: number | null = null;

    @yascField()
    @yascDisplayName('general_status')
    @yascTableIgnore(true)
    @yascFormKind('select')
    @yascOptionsKey('projectWorkflowStatuses')
    @yascRequired()
    @yascFormGrid({row: 3, width: 1})
    currentStatusId: number | null = null;

    @yascField()
    @yascDisplayName('general_status')
    @yascTableIndex(2)
    @yascTableColumn(ColumnType.Text)
    @yascTableDisplayFn(displayWorkflowStatus)
    @yascFormIgnore()
    currentWorkflowStatus?: WorkflowStatusMinRes;

    @yascField()
    @yascDisplayName('projectTableOrderNr')
    @yascTableIndex(4)
    @yascTableColumn(ColumnType.Text)
    @yascMaxLength(100)
    @yascFormGrid({row: 4, width: 1})
    orderNr = '';

    @yascField()
    @yascDisplayName('projectTableOfferNr')
    @yascTableIndex(5)
    @yascTableColumn(ColumnType.Text)
    @yascMaxLength(100)
    @yascFormGrid({row: 4, width: 1})
    offerNr = '';

    @yascField()
    @yascDisplayName('projectTableVolume')
    @yascTableIndex(6)
    @yascTableColumn(ColumnType.Decimal)
    @yascTableDisplayFn(formatCurrency)
    @yascRequired()
    @yascMin(0)
    @yascMax(1000000000)
    @yascFormGrid({row: 5, width: 1})
    projectVolumeBrutto = 0;

    @yascField()
    @yascDisplayName('projectTableRemainingAmount')
    @yascTableIndex(7)
    @yascTableColumn(ColumnType.Decimal)
    @yascTableDisplayFn(formatCurrency)
    @yascFormIgnore()
    projectVolumeBruttoOpen = 0;

    @yascField()
    @yascDisplayName('crm_projects_nextBill_tableHeader')
    @yascTableIndex(8)
    @yascTableColumn(ColumnType.Date)
    @yascFormKind('date')
    @yascFormGrid({row: 5, width: 1})
    nextBill?: string;

    @yascField()
    @yascDisplayName('projectTableStart')
    @yascTableIndex(9)
    @yascTableColumn(ColumnType.Date)
    @yascFormKind('date')
    @yascFormGrid({row: 6, width: 1})
    start?: string;

    @yascField()
    @yascDisplayName('projectTableEnd')
    @yascTableIndex(10)
    @yascTableColumn(ColumnType.Date)
    @yascFormKind('date')
    @yascFormGrid({row: 6, width: 1})
    end?: string;

    @yascField()
    @yascDisplayName('projectTableTeamMembers')
    @yascTableIndex(11)
    @yascTableColumn(ColumnType.Number)
    @yascFormIgnore()
    teamMemberCount = 0;

    @yascField()
    @yascDisplayName('general_active')
    @yascTableIgnore(true)
    @yascFormKind('checkbox')
    @yascFormGrid({row: 7, width: 1})
    isActive = true;

    @yascField()
    @yascDisplayName('projectRequireTasks')
    @yascTableIgnore(true)
    @yascFormKind('checkbox')
    @yascFormGrid({row: 7, width: 1})
    requireTasks = false;

    @yascField()
    @yascDisplayName('general_color')
    @yascTableIgnore(true)
    @yascMaxLength(7)
    @yascFormGrid({row: 8, width: 1})
    color?: string;

    @yascField()
    @yascDisplayName('general_description')
    @yascTableIgnore(true)
    @yascFormKind('textarea')
    @yascFormRows(4)
    @yascMaxLength(250)
    @yascFormGrid({row: 9, width: 2, fullRow: true})
    comment = '';

    @yascField()
    @yascDisplayName('projectTabTeamTitle')
    @yascSectionOrder(10)
    @yascNested(ProjectTeamMember)
    @yascCollection({
        itemIdKey: 'userId',
        createItem: () => new ProjectTeamMember(),
    })
    teamMembers: ProjectTeamMember[] = [];

    @yascField()
    @yascDisplayName('projectTableTask')
    @yascSectionOrder(20)
    @yascNested(ProjectTaskItem)
    @yascCollection({
        itemIdKey: 'id',
        readOnly: true,
        createItem: () => new ProjectTaskItem(),
    })
    tasks: ProjectTaskItem[] = [];

    @yascField()
    @yascDisplayName('projectTabInvoicesTitle')
    @yascSectionOrder(30)
    @yascNested(ProjectInvoiceItem)
    @yascCollection({
        itemIdKey: 'id',
        readOnly: true,
        createItem: () => new ProjectInvoiceItem(),
    })
    invoices: ProjectInvoiceItem[] = [];

    constructor(init: Partial<Project> = {}) {
        Object.assign(this, init);
        this.id = init.id ?? 0;
        this.name = init.name ?? '';
        this.internalName = init.internalName ?? '';
        this.customer = init.customer ?? null;
        this.customerId = init.customerId ?? init.customer?.id ?? null;
        this.workflowId = init.workflowId ?? null;
        this.currentWorkflowStatus = init.currentWorkflowStatus;
        this.currentStatusId = init.currentStatusId ?? init.currentWorkflowStatus?.id ?? null;
        this.orderNr = init.orderNr ?? '';
        this.offerNr = init.offerNr ?? '';
        this.comment = init.comment ?? '';
        this.projectVolumeBrutto = init.projectVolumeBrutto ?? 0;
        this.projectVolumeBruttoOpen = init.projectVolumeBruttoOpen ?? 0;
        this.teamMemberCount = init.teamMemberCount ?? init.teamMembers?.length ?? 0;
        this.isActive = init.isActive ?? true;
        this.requireTasks = init.requireTasks ?? false;
        this.teamMembers = (init.teamMembers ?? []).map(x => new ProjectTeamMember(x));
        this.tasks = (init.tasks ?? []).map(x => new ProjectTaskItem(x));
        this.invoices = (init.invoices ?? []).map(x => new ProjectInvoiceItem(x));
    }

    clone(changes: Partial<Project> = {}): Project {
        return new Project({
            ...this,
            ...changes,
            customer: changes.customer ?? this.customer,
            customerId: changes.customerId ?? this.customerId,
            currentWorkflowStatus: changes.currentWorkflowStatus ?? this.currentWorkflowStatus,
            teamMembers: changes.teamMembers ?? this.teamMembers,
            tasks: changes.tasks ?? this.tasks,
            invoices: changes.invoices ?? this.invoices,
        });
    }
}
